import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Printer, Thermometer, Flame, Layers, Timer, Gauge, Move3d,
    Wind, Percent, FileText, RefreshCw, WifiOff, Home, Ruler,
    Upload, ListOrdered, Play, Pause, Trash2, Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const POLL_MS = 2000;

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDuration(seconds) {
    if (seconds === null || seconds === undefined) return '--';
    const s = Math.max(0, Math.round(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
    return `${sec}s`;
}

function fmtNum(v, digits = 1, suffix = '') {
    if (v === null || v === undefined || Number.isNaN(v)) return '--';
    return `${Number(v).toFixed(digits)}${suffix}`;
}

// state (Klipper print_stats.state) -> visual config
const STATE_CFG = {
    printing:  { label: 'Impression en cours', color: 'text-green-500',  bg: 'bg-green-500/10',  dot: 'bg-green-500'  },
    paused:    { label: 'En pause',             color: 'text-amber-500',  bg: 'bg-amber-500/10',  dot: 'bg-amber-500'  },
    complete:  { label: 'Terminée',             color: 'text-blue-400',   bg: 'bg-blue-500/10',   dot: 'bg-blue-400'   },
    cancelled: { label: 'Annulée',              color: 'text-slate-400',  bg: 'bg-slate-500/10',  dot: 'bg-slate-400'  },
    standby:   { label: 'En veille',            color: 'text-slate-400',  bg: 'bg-slate-500/10',  dot: 'bg-slate-400'  },
    error:     { label: 'Erreur',               color: 'text-red-500',    bg: 'bg-red-500/10',    dot: 'bg-red-500'    },
};

function StateBadge({ state }) {
    const cfg = STATE_CFG[state] ?? { label: state || 'Inconnu', color: 'text-slate-400', bg: 'bg-slate-500/10', dot: 'bg-slate-400' };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${state === 'printing' ? 'animate-pulse' : ''}`} />
            {cfg.label}
        </span>
    );
}

// ── Small reusable stat tile ───────────────────────────────────────────────────

function Stat({ icon: Icon, label, value, sub, iconClass = 'text-primary' }) {
    return (
        <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Icon className={`w-4 h-4 ${iconClass}`} />
                <span className="text-xs font-medium">{label}</span>
            </div>
            <p className="text-2xl font-bold text-foreground tabular-nums">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
    );
}

// ── Heater card (temp + target + power bar) ────────────────────────────────────

function HeaterCard({ icon: Icon, iconClass, name, temp, target, power }) {
    const pct = Math.min(100, Math.max(0, (power ?? 0) * 100));
    const heating = (target ?? 0) > 0;
    return (
        <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${iconClass}`} />
                    <span className="font-semibold text-foreground text-sm">{name}</span>
                </div>
                {heating && <Flame className="w-4 h-4 text-orange-500 animate-pulse" />}
            </div>
            <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-foreground tabular-nums">{fmtNum(temp, 1)}</span>
                <span className="text-base text-muted-foreground">°C</span>
                <span className="text-sm text-muted-foreground ml-auto">
                    cible <strong className="text-foreground">{target > 0 ? fmtNum(target, 0) : '--'}{target > 0 ? '°' : ''}</strong>
                </span>
            </div>
            <div className="mt-3">
                <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>Puissance</span><span>{fmtNum(pct, 0, ' %')}</span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-orange-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
                </div>
            </div>
        </div>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function Printer3D() {
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const { showToast } = useToast();

    const [status, setStatus] = useState(null);
    const [info, setInfo] = useState(null);
    const [queue, setQueue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const canOperate = user?.role !== 'viewer';
    const fileInputRef = useRef(null);

    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);

    const authHeader = useCallback(() => ({ Authorization: `Bearer ${tokenRef.current}` }), []);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await fetch('/api/printer/status', { headers: authHeader() });
            if (res.ok) setStatus(await res.json());
        } catch (e) { console.error('fetchStatus', e); }
    }, [authHeader]);

    const fetchInfo = useCallback(async () => {
        try {
            const res = await fetch('/api/printer/info', { headers: authHeader() });
            if (res.ok) setInfo(await res.json());
        } catch (e) { console.error('fetchInfo', e); }
    }, [authHeader]);

    const fetchQueue = useCallback(async () => {
        try {
            const res = await fetch('/api/printer/queue', { headers: authHeader() });
            if (res.ok) setQueue(await res.json());
        } catch (e) { console.error('fetchQueue', e); }
    }, [authHeader]);

    useEffect(() => {
        let mounted = true;
        Promise.all([fetchStatus(), fetchInfo(), fetchQueue()]).finally(() => { if (mounted) setLoading(false); });
        const id = setInterval(() => { fetchStatus(); fetchQueue(); }, POLL_MS);
        const idInfo = setInterval(fetchInfo, POLL_MS * 15);
        return () => { mounted = false; clearInterval(id); clearInterval(idInfo); };
    }, [fetchStatus, fetchInfo, fetchQueue]);

    // ── Actions (operator only) ──────────────────────────────────────────────
    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) e.target.value = '';  // allow re-selecting the same file later
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            form.append('add_to_queue', 'true');
            const res = await fetch('/api/printer/upload', { method: 'POST', headers: authHeader(), body: form });
            if (res.ok) {
                showToast(`« ${file.name} » ajouté à la file`, 'success');
                fetchQueue();
            } else {
                const err = await res.json().catch(() => ({}));
                showToast(err.detail || 'Échec de l\'upload', 'error');
            }
        } catch (err) {
            showToast('Erreur réseau pendant l\'upload', 'error');
        } finally {
            setUploading(false);
        }
    };

    const removeJob = async (jobId) => {
        try {
            const res = await fetch('/api/printer/queue/remove', {
                method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ job_ids: [jobId] }),
            });
            if (res.ok) fetchQueue(); else showToast('Échec de la suppression', 'error');
        } catch { showToast('Erreur réseau', 'error'); }
    };

    const clearQueue = async () => {
        try {
            const res = await fetch('/api/printer/queue/remove', {
                method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ all: true }),
            });
            if (res.ok) { showToast('File vidée', 'success'); fetchQueue(); }
            else showToast('Échec', 'error');
        } catch { showToast('Erreur réseau', 'error'); }
    };

    const queueControl = async (action) => {
        try {
            const res = await fetch(`/api/printer/queue/${action}`, { method: 'POST', headers: authHeader() });
            if (res.ok) { showToast(action === 'start' ? 'File démarrée' : 'File en pause', 'success'); fetchQueue(); }
            else { const err = await res.json().catch(() => ({})); showToast(err.detail || 'Échec', 'error'); }
        } catch { showToast('Erreur réseau', 'error'); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />Chargement…
        </div>
    );

    const offline = !status || status.online === false;

    // Header
    const Header = (
        <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                    <Printer className="w-7 h-7 text-primary" />{t('nav.printer')}
                </h1>
                <p className="text-muted-foreground mt-1 text-sm">
                    {info?.hostname ? `${info.hostname}` : 'Klipper / Moonraker'}
                    {info?.software_version ? ` · v${info.software_version}` : ''}
                </p>
            </div>
            {!offline && <StateBadge state={status.state} />}
        </div>
    );

    if (offline) {
        return (
            <div className="space-y-6 pb-10">
                {Header}
                <div className="bg-card border border-dashed border-border rounded-xl p-10 text-center">
                    <WifiOff className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-foreground font-medium">Imprimante injoignable</p>
                    <p className="text-muted-foreground text-sm mt-1">
                        Impossible de contacter Moonraker{status?.moonraker_url ? ` (${status.moonraker_url})` : ''}.
                    </p>
                    <p className="text-muted-foreground text-xs mt-3">
                        Vérifiez que l'imprimante est allumée et sur le réseau. L'adresse se configure via la variable
                        d'environnement <code className="bg-muted px-1 rounded">MOONRAKER_URL</code>.
                    </p>
                </div>
            </div>
        );
    }

    const p = status.print || {};
    const progressPct = (p.progress ?? 0) * 100;
    const isPrinting = status.state === 'printing' || status.state === 'paused';
    const pos = status.toolhead?.position || [];
    const gm = status.gcode_move || {};

    return (
        <div className="space-y-6 pb-10">
            {Header}

            {/* ── Current print ── */}
            <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-4 h-4 text-primary shrink-0" />
                    <span className="font-semibold text-foreground truncate">
                        {p.filename || (isPrinting ? 'Impression sans nom' : 'Aucune impression en cours')}
                    </span>
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-primary transition-[width] duration-700 ease-out" style={{ width: `${progressPct}%` }} />
                    </div>
                    <span className="text-lg font-bold text-foreground tabular-nums w-16 text-right">{fmtNum(progressPct, 1, ' %')}</span>
                </div>

                {/* Print sub-stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    <div>
                        <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1"><Layers className="w-3.5 h-3.5" />Couche</p>
                        <p className="text-lg font-semibold text-foreground tabular-nums">
                            {p.current_layer != null && p.total_layer != null ? `${p.current_layer} / ${p.total_layer}` : '--'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1"><Timer className="w-3.5 h-3.5" />Écoulé</p>
                        <p className="text-lg font-semibold text-foreground tabular-nums">{fmtDuration(p.print_duration)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1"><Timer className="w-3.5 h-3.5" />Restant (est.)</p>
                        <p className="text-lg font-semibold text-foreground tabular-nums">{fmtDuration(p.estimated_time_left)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1"><Ruler className="w-3.5 h-3.5" />Filament</p>
                        <p className="text-lg font-semibold text-foreground tabular-nums">
                            {p.filament_used != null ? fmtNum(p.filament_used / 1000, 2, ' m') : '--'}
                        </p>
                    </div>
                </div>
                {p.message && <p className="text-xs text-muted-foreground mt-4 text-center italic">{p.message}</p>}
            </div>

            {/* ── Heaters ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <HeaterCard icon={Thermometer} iconClass="text-orange-400" name="Buse (extrudeur)"
                    temp={status.extruder?.temp} target={status.extruder?.target} power={status.extruder?.power} />
                <HeaterCard icon={Thermometer} iconClass="text-blue-400" name="Plateau (lit chauffant)"
                    temp={status.bed?.temp} target={status.bed?.target} power={status.bed?.power} />
            </div>

            {/* ── Machine stats ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <Stat icon={Gauge} label="Vitesse" iconClass="text-primary"
                    value={fmtNum(gm.speed != null ? gm.speed / 60 : null, 0)} sub="mm/s" />
                <Stat icon={Percent} label="Débit vitesse" iconClass="text-primary"
                    value={fmtNum(gm.speed_factor != null ? gm.speed_factor * 100 : null, 0, ' %')} />
                <Stat icon={Percent} label="Débit filament" iconClass="text-primary"
                    value={fmtNum(gm.extrude_factor != null ? gm.extrude_factor * 100 : null, 0, ' %')} />
                <Stat icon={Wind} label="Ventilateur" iconClass="text-cyan-400"
                    value={fmtNum(status.fan_speed != null ? status.fan_speed * 100 : null, 0, ' %')} />
                <Stat icon={Home} label="Axes homés" iconClass="text-green-500"
                    value={status.toolhead?.homed_axes ? status.toolhead.homed_axes.toUpperCase() || '--' : '--'} />
                <Stat icon={Move3d} label="Position" iconClass="text-primary"
                    value={pos.length >= 3 ? `Z ${fmtNum(pos[2], 2)}` : '--'}
                    sub={pos.length >= 2 ? `X ${fmtNum(pos[0], 1)} · Y ${fmtNum(pos[1], 1)}` : null} />
            </div>

            {/* ── Job queue ── */}
            {(() => {
                const jobs = queue?.queued_jobs || [];
                const qState = queue?.queue_state;
                const QSTATE = {
                    ready:    { label: 'Prête',      cls: 'text-blue-400 bg-blue-500/10' },
                    paused:   { label: 'En pause',   cls: 'text-amber-500 bg-amber-500/10' },
                    loading:  { label: 'Chargement', cls: 'text-slate-400 bg-slate-500/10' },
                    starting: { label: 'Démarrage',  cls: 'text-green-500 bg-green-500/10' },
                };
                const qcfg = QSTATE[qState];
                return (
                    <div className="bg-card border border-border rounded-xl p-5">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                            <div className="flex items-center gap-2">
                                <ListOrdered className="w-4 h-4 text-primary" />
                                <span className="font-semibold text-foreground">File d'attente</span>
                                <span className="text-xs text-muted-foreground">({jobs.length})</span>
                                {qcfg && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${qcfg.cls}`}>{qcfg.label}</span>}
                            </div>
                            {canOperate && (
                                <div className="flex items-center gap-2">
                                    <input ref={fileInputRef} type="file" accept=".gcode,.gco,.g,.ufp" onChange={handleUpload} className="hidden" />
                                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
                                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                        {uploading ? 'Envoi…' : 'Uploader'}
                                    </button>
                                    {qState === 'paused' || qState == null ? (
                                        <button onClick={() => queueControl('start')} disabled={jobs.length === 0}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 text-sm font-medium disabled:opacity-40 transition-colors">
                                            <Play className="w-4 h-4" />Démarrer
                                        </button>
                                    ) : (
                                        <button onClick={() => queueControl('pause')}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 text-sm font-medium transition-colors">
                                            <Pause className="w-4 h-4" />Pause
                                        </button>
                                    )}
                                    {jobs.length > 0 && (
                                        <button onClick={clearQueue} title="Vider la file"
                                            className="p-1.5 rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-400 transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {jobs.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <ListOrdered className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">File vide</p>
                                {canOperate && <p className="text-xs mt-1">Uploadez un fichier .gcode pour l'ajouter.</p>}
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {jobs.map((job, idx) => (
                                    <div key={job.job_id} className="flex items-center gap-3 bg-muted/40 rounded-lg px-3 py-2.5">
                                        <span className="text-xs font-mono text-muted-foreground w-5 text-center shrink-0">{idx + 1}</span>
                                        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <span className="text-sm text-foreground truncate flex-1" title={job.filename}>
                                            {(job.filename || '').split('/').pop()}
                                        </span>
                                        {canOperate && (
                                            <button onClick={() => removeJob(job.job_id)} title="Retirer"
                                                className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Klipper state message */}
            {status.state_message && status.klipper_state !== 'ready' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-sm text-amber-500">
                    {status.state_message}
                </div>
            )}
        </div>
    );
}
